/**
 * @file     ProseBlock.tsx
 * @purpose  Renders a sanitized prose block from the content engine with long-form reading typography (~65ch measure).
 */
'use client';

import Box from '@mui/material/Box';
import { useAppTheme } from '@/core/theme/useAppTheme';

type ProseBlockProps = { html: string };

export const ProseBlock = ({ html }: ProseBlockProps) => {
  const { theme } = useAppTheme();

  return (
    <Box
      dangerouslySetInnerHTML={{ __html: html }}
      sx={{
        maxWidth: '65ch',
        '& p': {
          fontSize: theme.typography.bodyLg,
          lineHeight: 1.8,
          color: theme.semanticColors.textSecondary,
          m: '0 0 18px',
        },
        '& p:last-of-type': { mb: 0 },
        '& a': {
          color: theme.semanticColors.interactivePrimary,
          textDecoration: 'none',
          borderBottom: `1px solid ${theme.semanticColors.borderSubtle}`,
          transition: 'border-color 180ms ease',
          '&:hover': { borderBottomColor: theme.semanticColors.interactivePrimary },
          '&:focus-visible': {
            outline: `2px solid ${theme.semanticColors.interactiveFocusRing}`,
            outlineOffset: '2px',
            borderRadius: `${theme.layout.radius.sm}px`,
          },
        },
        '& strong': { color: theme.semanticColors.textPrimary, fontWeight: 600 },
      }}
    />
  );
};
